import { FC, Fragment, useEffect, useMemo, useCallback } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
// Types
import UserRoles from 'app/types/UserRoles';
// Models
import { IMyUser } from 'app/models/User';
import IEpisode from 'app/models/Episode';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// ASync
import { getEpisode, createEpisode, updateEpisode } from 'app/store/Episodes/Episodes.async';
import { createFact } from 'app/store/EpisodeFacts/EpisodeFacts.async';
// Actions
import MixpanelActions from 'app/features/mixpanel/MixpanelActions';
import { EpisodesActions } from 'app/store/Episodes/Episodes.slice';
import { EpisodeFactsActions } from 'app/store/EpisodeFacts/EpisodeFacts.slice';
// Selectors
import { selectTrialMode } from 'app/store/AppUI/AppUI.selectors';
import { selectMyUser } from 'app/store/Users/Users.selectors';
import { selectLoading, selectStatusCode, selectStatusMessage } from 'app/store/Episodes/Episodes.selectors';
import { selectEpisodeFactsWithNegativeId } from 'app/store/EpisodeFacts/EpisodeFacts.selectors';
// Mui
import { Box, FormHelperText, Typography, Alert } from '@mui/material';
// Dialogs
import ConfirmationDialog from 'app/dialogs/ConfirmationDialog';
// Components
import { Button } from 'app/components/Mui';
// Hooks
import useCaseStatus from 'app/hooks/useCaseStatus';
import useToggle from 'app/hooks/useToggle';
import useAccountStatus from 'app/hooks/useAccountStatus';
// Context
import { usePageEpisodesContext } from 'app/context/PageEpisodes.context';
// Utilities
import { dateToString, parseDate, parseTime, timeToString } from 'app/utilities/DateTime';
// 
import EpisodeFormLabels from './EpisodeFormLabels';
import EpisodeFormFacts from './EpisodeFormFacts';
import EpisodeFormDates from './EpisodeFormDates';
import EpisodeFormDateTime from './EpisodeFormDateTime';
// import EpisodeAuthorTypeControls from './AuthorTypeGroup/EpisodeAuthorTypeControls';
import EpisodeFormPageNum from './EpisodeFormPageNum';
import EpisodeFormNotes from './EpisodeFormNotes';
import EpisodeFormTogglers from './EpisodeFormTogglers';
import AuthorTypeGroup from './AuthorTypeGroup';
// import SuggestedAuthorsList from './AuthorTypeGroup/SuggestedAuthorsList';
// ToDO
import { IPage } from '@root/models/Page';
import PageActions from 'app/store/page/page.actions';
import { selectPage, selectPageStaples } from 'app/store/page/page.selectors';
import { generateWorkspaceOrder } from 'app/store/page/page.service';
import { validateAuthorAndType } from 'app/store/EpisodeAuthors/EpisodeAuthors.async';
// End: ToDO

interface IFormData {
  labels: string[];
  author: any;
  type: string;
  date: Dayjs | null;
  time: Dayjs | null;
  pageNum: string | null;
  notes: string;
  preEvent: boolean;
  showInTimeline: boolean;
};

type Props = {
  episode?: Omit<IEpisode, 'id'> & {
    id: number | undefined
  } | undefined;
  onClose?: () => void;
  isParentEpisodeCreation?: boolean;
}

const EpisodeForm:FC<Props> = ({
  // Props
  episode, onClose, isParentEpisodeCreation
}) => {
  const isEpisodeCreation = !episode || typeof episode.id === 'undefined';

  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const trialMode = useAppSelector(selectTrialMode);
  const myUser = useAppSelector(selectMyUser) as IMyUser;
  // ToDO
  const page = useAppSelector((state) => selectPage(state as any)) as IPage;
  const pageStaples = useAppSelector((state) => selectPageStaples(state as any));
  // End: ToDO
  const loading = useAppSelector(selectLoading);
  const statusCode = useAppSelector(selectStatusCode);
  const statusMessage = useAppSelector(selectStatusMessage);
  const episodeFactsWithNegativeId = useAppSelector(selectEpisodeFactsWithNegativeId);

  const { isArchived } = useCaseStatus();
  const { isSuspended } = useAccountStatus();
  const { open, toggle } = useToggle();

  const { onCreate, onUpdate, isPageRoute } = usePageEpisodesContext();

  const pagesNums = useMemo(() => {
    if ( !pageStaples ) return [];
    return pageStaples.map((page:IPage) => page.pageNum.toString());
  }, [pageStaples])

  const methods = useForm<IFormData>({
    defaultValues: {
      labels: episode?.labels || [],
      author: episode?.author || null,
      type: episode?.type || '',
      date: episode?.date ? parseDate(episode.date) : null,
      time: episode?.time ? parseTime(episode.time) : null,
      pageNum: episode?.pageNum ? episode.pageNum.toString() : null,
      notes: episode?.notes || '',
      preEvent: episode?.preEvent || false,
      showInTimeline: !episode || episode.showInTimeline ? true : false
    }
  });

  const { register, handleSubmit, formState:{ isSubmitted }, watch } = methods;

  const generateEpisodeData = useCallback((data:IFormData) => {
    const { labels, date, time, pageNum, notes, type, ...restData } = data;
    const episodeData:any = {...restData, documentId: page.documentId };
    if ( labels.length ) episodeData['labels'] = labels;
    if ( date ){
      episodeData['date'] = dateToString(date);
      if ( time ) episodeData['time'] = timeToString(time);
    }
    if ( pageNum && pagesNums.includes(pageNum) ){
      episodeData['pageNum'] = Number(pageNum);
    } else {
      episodeData['pageNum'] = page.pageNum;
    }
    if ( type ) episodeData['type'] = type;
    if ( notes ) episodeData['notes'] = notes;
    return episodeData;
    // eslint-disable-next-line
  }, []);

  const onSubmit = handleSubmit((data:IFormData) => {
    if ( isAnyRequired ) return;

    const episodeData = generateEpisodeData(data);

    if ( isEpisodeCreation ){
      asyncCreateEpisode(episodeData);
    } else {
      if ( !episode.id ) return;
      episodeData['version'] = episode.version;
      asyncUpdateEpisode(episode.id, episodeData);
    }
  });

  const asyncCreateEpisode = async (data:any) => {
    try {
      if ( data.author?.name ){
        const validatedAuthor = await dispatch(validateAuthorAndType({ author: data.author, type: data.type })).unwrap();
        data['author'] = validatedAuthor;
        data['authorId'] = validatedAuthor.id;
      }

      const episode = await dispatch(createEpisode(data)).unwrap();
      if ( isParentEpisodeCreation ){
        asyncPatchPage(episode.id, episode.date, episode.time);
      }
      asyncCreateFacts(episode.id);

      if ( onClose ) onClose();

      onCreate(episode);
    } catch(error){}
  }

  const asyncUpdateEpisode = async (episodeId:number, data:any) => {
    try {
      if ( data.author?.name ){
        const validatedAuthor = await dispatch(validateAuthorAndType({ author: data.author, type: data.type })).unwrap();
        data['author'] = validatedAuthor;
        data['authorId'] = validatedAuthor.id;
      }

      const episode = await dispatch(updateEpisode({ episodeId, data })).unwrap();
      if ( episodeId === page.parentEpisodeId ){
        asyncPatchPage(episodeId, episode.date, episode.time);
      }

      onUpdate(episode);
    } catch(error){}
  }

  const asyncCreateFacts = async (episodeId:number) => {
    if ( !episodeFactsWithNegativeId || !episodeFactsWithNegativeId.length ) return;

    try {
      for ( let fact of episodeFactsWithNegativeId ){
        await dispatch(createFact({ ...fact, episodeId })).unwrap();
      }
      dispatch(EpisodeFactsActions.clearFactsWithNegativeId());
    } catch(error){}
  }

  // ToDO
  const asyncPatchPage = async (parentEpisodeId:number | undefined, date:string | undefined, time:string | undefined) => {
    // Patch page
    const nextPageData:any = {
      version: page.version,
      parentEpisodeId
    };
    if ( page.inWorkspace ){
      nextPageData['workspaceOrder'] = generateWorkspaceOrder(null, {
        workspaceOrder: page.workspaceOrder,
        date,
        time
      });
    }
    return new Promise((resolve) => {
      dispatch(PageActions.patchPage(page.documentId, page.pageNum, nextPageData));
      setTimeout(() => {
        resolve(`Page updated`)
      }, 500);
    });
  }

  useEffect(() => {
    if ( statusCode === 409 ){
      toggle();

      if ( episode && episode.id ){
        dispatch(MixpanelActions.handleCaseEpisodeUpdateConflict({ episodeId: episode.id }));
      }
    }
    // eslint-disable-next-line
  }, [statusCode]);

  const handleClick = () => {
    if ( typeof onClose !== 'undefined' ){
      onClose();
    } else {
      dispatch(EpisodesActions.setSelectedEpisodeId(null));
    }
    dispatch(EpisodeFactsActions.clearFactsWithNegativeId());
  }

  const handleCancel = () => {
    if ( open ) toggle();

    dispatch(EpisodesActions.setStatusError({
      statusCode: null,
      statusMessage: null
    }));
  }

  const onConfirm = async (data:IFormData) => {
    const nextData = generateEpisodeData(data);

    if ( !episode || !episode.id ) return;

    try {
      const { version } = await dispatch(getEpisode(episode.id)).unwrap();
      nextData['version'] = version;
      await asyncUpdateEpisode(episode.id, nextData);

      dispatch(EpisodesActions.setStatusError({
        statusCode: null,
        statusMessage: null
      }));

      if ( open ) toggle();
    } catch(error){}
  }

  const watchAuthor = watch('author');
  const watchType = watch('type');
  const watchDate = watch('date');

  const myRolePresenterOrClient = myUser.role === UserRoles.Presenter || myUser.role === UserRoles.Client;

  const isDisabled = Boolean((myRolePresenterOrClient || trialMode) || isArchived || isSuspended || (page.staple && page.staple.order !== 1 && !isPageRoute));
  const isAnyRequired = !watchAuthor && !watchType && !watchDate;

  return (
    <Fragment>
      <FormProvider {...methods}>
        <form onSubmit={onSubmit}>
          <input {...register('labels') as any} type="hidden" />

          <EpisodeFormLabels episodeId={episode?.id} episodeVersion={episode?.version} isDisabled={isDisabled} />
          <EpisodeFormFacts isDisabled={isDisabled} />
          <EpisodeFormDates isDisabled={isDisabled} />

          <AuthorTypeGroup ControlProps={{ disabled: isDisabled }} />
          {isSubmitted && isAnyRequired ? (
            <FormHelperText sx={{ mb: 2 }} error={true}>{t('dialogs.episodeForm.shouldBeSpecified')}</FormHelperText>
          ) : null}
          <EpisodeFormDateTime isDisabled={isDisabled} />
          <EpisodeFormPageNum pagesNums={pagesNums} isDisabled={isDisabled} />
          <EpisodeFormNotes episodeId={episode?.id} isDisabled={isDisabled} />
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <EpisodeFormTogglers isDisabled={isDisabled} />
            {!isDisabled ? (
              <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                {isEpisodeCreation ? (
                  <Button
                    name="Cancel create episode form dialog"
                    color="primary"
                    size="small"
                    onClick={handleClick}
                  >{t('buttons.close')}</Button>
                ) : null}
                <Button
                  loading={loading}
                  name={isEpisodeCreation ? 'Create episode form dialog' : 'Save episode form'}
                  type="submit"
                  color="primary"
                  variant="contained"
                  size="small"
                  // disabled={authorsIsFetching}
                >{isEpisodeCreation ? t('dialogs.episodeForm.createEpisodeButton') : t('dialogs.episodeForm.saveEpisodeButton')}</Button>
              </Box>
            ) : null}
          </Box>
        </form>
      </FormProvider>
      {open ? (
        <ConfirmationDialog
          open={open}
          onClose={handleCancel}
          title={t('dialogs.episodeForm.confirmationTitle')}
          content={
            <Fragment>
              {statusMessage ? (
                <Alert sx={{ mb: 4 }} color="info">{statusMessage}</Alert>
              ) : null}
              <Typography variant="body1">{t('dialogs.episodeForm.confirmationContent')}</Typography>
            </Fragment>
          }
          loading={loading}
          onConfirm={handleSubmit(onConfirm)}
          confirmLabel={t('buttons.continueEditing')}
          closeLabel={t('buttons.overrideRemoteChanges')}
        />
      ) : null}
    </Fragment>
  )
}

export default EpisodeForm;
